import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/PageHeader';
import {
  useClients,
  useCreateMember,
  useResetMemberPassword,
  useTeam,
  useUpdateMember,
} from '../hooks/useOps';
import type { OpsUser } from '../api/types';
import { formatDate } from '../lib/format';

const EMPTY = {
  username: '',
  password: '',
  name: '',
  email: '',
  role: 'operator',
  clientId: '',
  telegramUserId: '',
};

const ROLE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'super_admin', label: 'super admin — full control, incl. the agent brain' },
  { value: 'admin', label: 'admin — team, projects, track types, agent keys + kill-switch' },
  { value: 'operator', label: 'operator — operate records + edit project tracks/progress' },
  { value: 'client', label: 'client — customer portal only (their project overviews)' },
];

export function TeamPage() {
  const { data: members = [], isLoading, error } = useTeam();
  const { data: clients = [] } = useClients();
  const create = useCreateMember();
  const update = useUpdateMember();
  const resetPassword = useResetMemberPassword();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<OpsUser | null>(null);
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState<string | null>(null);

  const [resetFor, setResetFor] = useState<OpsUser | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState(false);

  const openAdd = () => {
    setEditing(null);
    setForm(EMPTY);
    setFormError(null);
    setOpen(true);
  };
  const openEdit = (u: OpsUser) => {
    setEditing(u);
    setForm({
      username: u.username,
      password: '',
      name: u.name,
      email: u.email ?? '',
      role: u.role,
      clientId: u.clientId ?? '',
      telegramUserId: u.telegramUserId ?? '',
    });
    setFormError(null);
    setOpen(true);
  };

  const openReset = (u: OpsUser) => {
    setResetFor(u);
    setNewPassword('');
    setResetError(null);
    setResetDone(false);
  };

  const submit = async () => {
    setFormError(null);
    // Customer link only travels with the client role; otherwise it is cleared.
    const clientId = form.role === 'client' && form.clientId ? form.clientId : null;
    try {
      if (editing) {
        await update.mutateAsync({
          id: editing.id,
          body: {
            username: form.username.trim(),
            name: form.name.trim(),
            email: form.email.trim() || undefined,
            role: form.role,
            clientId,
            telegramUserId: form.telegramUserId.trim() || undefined,
          },
        });
      } else {
        await create.mutateAsync({
          username: form.username.trim(),
          password: form.password,
          name: form.name.trim(),
          email: form.email.trim() || undefined,
          role: form.role,
          clientId,
          telegramUserId: form.telegramUserId.trim() || undefined,
        });
      }
      setOpen(false);
    } catch (e) {
      setFormError((e as Error).message);
    }
  };

  const submitReset = async () => {
    if (!resetFor) return;
    setResetError(null);
    try {
      await resetPassword.mutateAsync({ id: resetFor.id, password: newPassword });
      setResetDone(true);
    } catch (e) {
      setResetError((e as Error).message);
    }
  };

  const toggleActive = (u: OpsUser) =>
    update.mutate({ id: u.id, body: { active: !u.active } });

  const columns: GridColDef<OpsUser>[] = [
    { field: 'username', headerName: 'Username', minWidth: 120, flex: 0.8 },
    { field: 'name', headerName: 'Name', minWidth: 140, flex: 1 },
    { field: 'email', headerName: 'Email', minWidth: 170, flex: 1, valueGetter: (v) => v ?? '—' },
    {
      field: 'role',
      headerName: 'Role',
      width: 110,
      renderCell: (p) => (
        <Stack direction="row" gap={0.5} alignItems="center" sx={{ height: '100%' }}>
          <Chip
            size="small"
            label={p.value}
            color={p.value === 'super_admin' ? 'primary' : 'default'}
            variant="outlined"
          />
          {p.row.permissions && Object.keys(p.row.permissions).length > 0 && (
            <Chip size="small" label="custom" color="warning" variant="outlined" />
          )}
        </Stack>
      ),
    },
    {
      field: 'telegramUserId',
      headerName: 'Telegram id',
      width: 130,
      valueGetter: (v) => v ?? '—',
    },
    {
      field: 'active',
      headerName: 'Status',
      width: 100,
      renderCell: (p) =>
        p.value ? (
          <Chip size="small" color="success" variant="outlined" label="Active" />
        ) : (
          <Chip size="small" color="default" variant="outlined" label="Inactive" />
        ),
    },
    {
      field: 'lastLoginAt',
      headerName: 'Last login',
      width: 130,
      valueGetter: (v) => (v ? formatDate(v as string) : '—'),
    },
    {
      field: 'actions',
      headerName: '',
      width: 320,
      sortable: false,
      renderCell: (p) => (
        <Stack direction="row" gap={0.5}>
          <Button size="small" onClick={() => openEdit(p.row)}>
            Edit
          </Button>
          <Button size="small" onClick={() => navigate(`/team/${p.row.id}/permissions`)}>
            Permissions
          </Button>
          <Button size="small" onClick={() => openReset(p.row)}>
            Reset password
          </Button>
          <Button size="small" color={p.row.active ? 'error' : 'success'} onClick={() => toggleActive(p.row)}>
            {p.row.active ? 'Deactivate' : 'Activate'}
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Team"
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add member
          </Button>
        }
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        Login is local: each member signs in with the username + password set here.{' '}
        <strong>Founder</strong> = everything; <strong>finance</strong> = cash, approvals,
        kill-switch, payroll; <strong>ops</strong> = operations, HR directory, leave decisions;{' '}
        <strong>viewer</strong> = read-only; <strong>client</strong> = a customer login that only
        sees the portal with their own project overviews (pick the customer below — no money, no
        internal pages). Use <strong>Permissions</strong> to grant or revoke
        individual capabilities beyond the role default. Link a Telegram id to let that person
        drive the agent as themselves (copilot mode).
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 560 }}>
        <DataGrid
          rows={members}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Paper>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add team member'}</DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
          <Stack gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Username"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              helperText="Login name: letters, digits, dot, underscore, dash."
              fullWidth
              autoFocus={!editing}
            />
            {!editing && (
              <TextField
                label="Password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                helperText="Min 8 characters. The member can be given a new one later via Reset password."
                fullWidth
              />
            )}
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
            />
            <TextField
              label="Email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              fullWidth
            />
            <TextField
              select
              label="Role"
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              fullWidth
            >
              {ROLE_OPTIONS.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {r.label}
                </MenuItem>
              ))}
            </TextField>
            {form.role === 'client' && (
              <TextField
                select
                label="Customer"
                value={form.clientId}
                onChange={(e) => setForm({ ...form, clientId: e.target.value })}
                helperText="The portal shows only this customer's projects."
                fullWidth
              >
                {clients.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              label="Telegram user id (optional)"
              value={form.telegramUserId}
              onChange={(e) => setForm({ ...form, telegramUserId: e.target.value })}
              helperText="Links a chat user to this identity for copilot mode."
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              create.isPending ||
              update.isPending ||
              form.name.trim().length < 2 ||
              form.username.trim().length < 3 ||
              (!editing && form.password.length < 8) ||
              (form.role === 'client' && !form.clientId)
            }
            onClick={submit}
          >
            {editing ? 'Save' : 'Add member'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetFor != null} onClose={() => setResetFor(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset password — {resetFor?.name}</DialogTitle>
        <DialogContent>
          {resetError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {resetError}
            </Alert>
          )}
          {resetDone ? (
            <Alert severity="success" sx={{ mt: 1 }}>
              Password updated. Share it with {resetFor?.name} securely.
            </Alert>
          ) : (
            <TextField
              label="New password"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Min 8 characters."
              fullWidth
              autoFocus
              sx={{ mt: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetFor(null)}>{resetDone ? 'Close' : 'Cancel'}</Button>
          {!resetDone && (
            <Button
              variant="contained"
              disabled={resetPassword.isPending || newPassword.length < 8}
              onClick={submitReset}
            >
              Reset password
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
